import { useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { KeyRound, Check, X, ChevronDown, ChevronUp } from 'lucide-react';

// Role → plain-English access description
const ACCESS_MATRIX = {
  super_user: {
    label: 'Super User',
    tagline: 'Full access — you can see and do everything.',
    can: [
      'View & edit every client, clinic, and clinician',
      'Approve new users and change any role',
      'View the Executive BI dashboard and edit payer rates',
      'Run Data Import, OD Digest, Setup & Dropdowns',
    ],
    cant: [],
  },
  admin: {
    label: 'Admin',
    tagline: 'Full ops access — everything except Executive BI, Payroll, and destructive super-user tools.',
    can: [
      'Edit every client, clinic, and clinician across all 9 locations',
      'Run Data Import, Weekly OD Digest, Setup & Dropdowns',
      'Access Admin Vault and Underpayment Recovery',
    ],
    cant: [
      'View the Executive BI dashboard (Super User only)',
      'Run Payroll (HR / Super User only)',
      'Approve users, change roles, edit rates, wipe data, or manage backups (Super User only)',
    ],
  },
  hr_payroll: {
    label: 'HR / Payroll',
    tagline: 'Runs payroll plus everyday ops — no BI, Vault, or Underpayment access.',
    can: [
      'Full Payroll module — create/edit runs, save summaries, finalize payroll',
      'Dashboard, Client Pipeline, Inquiries, Clinician Roster & Performance',
      'Data Import, OD Digest, Setup & Dropdowns, Pending Notes',
    ],
    cant: [
      'View the Executive BI dashboard',
      'Open the Admin Vault (provider credentials + payer directory)',
      'Access the Underpayment Recovery module',
      'Approve users, change roles, edit rates, wipe data, or manage backups (Super User only)',
    ],
  },
  office_director: {
    label: 'Office Director',
    tagline: 'Your clinic, your team — scoped to your assigned location.',
    can: [
      'View Dashboard, Client Pipeline, Today\'s Actions, Pending Notes, Feedback',
      'Open your own Clinic tab and edit its config, availability & outside locations',
      'Add / edit / delete clinicians in your clinic and view their Performance',
    ],
    cant: [
      'See other clinics\' tabs or reassign clinicians outside your clinic',
      'Run Data Import, OD Digest, Setup, or approve users',
      'Access Executive BI',
    ],
  },
  read_only: {
    label: 'Read Only',
    tagline: 'Full read/write on client info · view-only on clinic config.',
    can: [
      'Browse Dashboard, Client Pipeline, Today\'s Actions, Pending Notes, Feedback',
      'View any Clinic tab and the Clinician Roster',
    ],
    cant: [
      'Edit clients, clinicians, or clinic config',
      'Run Data Import, OD Digest, Setup, User Management',
      'View the Executive BI dashboard',
    ],
  },
  pending: {
    label: 'Pending Approval',
    tagline: 'A Super User needs to approve you before you can do anything.',
    can: [],
    cant: ['Everything until your role is set'],
  },
};

export default function AccessCheatSheet() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const meta = ACCESS_MATRIX[user.role] || ACCESS_MATRIX.pending;
  const clinicsLine = user.clinics?.length
    ? user.clinics.join(', ')
    : (user.clinic || (['super_user', 'admin', 'hr_payroll', 'read_only'].includes(user.role) ? 'All 9 clinics' : '—'));

  return (
    <div
      className="rounded-2xl border border-[#E8E5EC] bg-white/70 backdrop-blur-sm p-4"
      data-testid="access-cheat-sheet"
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-start gap-3 text-left"
        data-testid="access-cheat-toggle"
        aria-expanded={open}
      >
        <div className="rounded-lg p-2 shrink-0" style={{ background: '#BAA9CA33' }}>
          <KeyRound size={16} strokeWidth={2} className="text-[#685B72]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">You have access to</div>
          <div className="font-serif text-lg text-[#2D2833] mt-0.5 leading-snug" data-testid="access-cheat-tagline">
            {meta.label} · {meta.tagline}
          </div>
          <div className="text-[11px] text-[#685B72] mt-0.5 truncate">
            Signed in as <span className="font-semibold text-[#685B72]">{user.name}</span> · Clinics: <span className="font-semibold">{clinicsLine}</span>
          </div>
        </div>
        <div className="shrink-0 text-[#685B72] mt-1">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="access-cheat-details">
          <div className="rounded-xl border border-[#839E97]/40 bg-[#839E97]/10 p-3">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#839E97]">
              <Check size={12} strokeWidth={2.4} /> You can
            </div>
            <ul className="mt-2 space-y-1.5">
              {meta.can.length === 0 && <li className="text-[12px] text-[#685B72]">Nothing yet — waiting on approval.</li>}
              {meta.can.map((line, i) => (
                <li key={i} className="text-[12px] text-[#2D2833] leading-snug flex gap-1.5">
                  <Check size={12} className="text-[#839E97] mt-0.5 shrink-0" strokeWidth={2.4} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-[#8A3A3A]/30 bg-[#8A3A3A]/08 p-3">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#8A3A3A]">
              <X size={12} strokeWidth={2.4} /> You can't
            </div>
            <ul className="mt-2 space-y-1.5">
              {meta.cant.length === 0 && <li className="text-[12px] text-[#839E97]">No restrictions — you're the boss.</li>}
              {meta.cant.map((line, i) => (
                <li key={i} className="text-[12px] text-[#2D2833] leading-snug flex gap-1.5">
                  <X size={12} className="text-[#8A3A3A] mt-0.5 shrink-0" strokeWidth={2.4} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
